/**
 * Quote Notification Service
 * Handles notifications for RFQs, quotes, and important events
 */

import { toast } from "sonner";
import type { Quote, Lead, RFQProcessingResult } from "@/types/quotes";

export interface Notification {
  id: string;
  type: 'rfq_received' | 'quote_needs_review' | 'quote_sent' | 'quote_accepted' | 'quote_rejected' | 'quote_expiring' | 'high_value_quote' | 'export_controlled';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  quote_id?: string;
  lead_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
  read: boolean;
}

export class QuoteNotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];

  /**
   * Notify when new RFQ email is processed
   */
  notifyRFQReceived(result: RFQProcessingResult) {
    const { lead, quote, confidence_score, requires_manual_review } = result;

    if (!lead || !quote) return;

    const priority = requires_manual_review ? 'high' : 'medium';

    const notification: Notification = {
      id: `rfq-${Date.now()}`,
      type: 'rfq_received',
      title: '📧 New RFQ Processed',
      message: `RFQ from ${lead.company_name} (${lead.country || 'N/A'}) processed. ${requires_manual_review ? '⚠️ Requires manual review.' : 'Quote draft created.'}`,
      priority,
      quote_id: quote.id,
      lead_id: lead.id,
      metadata: {
        company_name: lead.company_name,
        lead_score: lead.lead_score,
        quote_amount: quote.total_amount,
        confidence_score,
        requires_review: requires_manual_review,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    // Show toast
    if (requires_manual_review) {
      toast.warning(`New RFQ from ${lead.company_name} needs review`, {
        description: `Quote ${quote.quote_number} • $${quote.total_amount?.toLocaleString()}`,
        action: {
          label: "View Quote",
          onClick: () => window.location.href = `/quotes/${quote.id}`,
        },
      });
    } else {
      toast.success(`New RFQ processed: ${lead.company_name}`, {
        description: `Quote ${quote.quote_number} created`,
      });
    }

    // Additional alerts for high-value or export-controlled
    if (quote.total_amount && quote.total_amount > 100000) {
      this.notifyHighValueQuote(quote);
    }

    if (quote.requires_export_license) {
      this.notifyExportControlled(quote);
    }
  }

  /**
   * Notify when quote needs manual review
   */
  notifyQuoteNeedsReview(quote: Quote, reasons: string[]) {
    const notification: Notification = {
      id: `review-${Date.now()}`,
      type: 'quote_needs_review',
      title: '⚠️ Quote Needs Review',
      message: `Quote ${quote.quote_number} for ${quote.customer_company_name} requires manual review. Reasons: ${reasons.join(', ')}`,
      priority: 'high',
      quote_id: quote.id,
      metadata: {
        reasons,
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        amount: quote.total_amount,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.warning(`Quote ${quote.quote_number} needs review`, {
      description: reasons.slice(0, 2).join(', '),
      action: {
        label: "Review Now",
        onClick: () => window.location.href = `/quotes/${quote.id}`,
      },
      duration: 10000,
    });
  }

  /**
   * Notify when quote is sent to customer
   */
  notifyQuoteSent(quote: Quote) {
    const notification: Notification = {
      id: `sent-${Date.now()}`,
      type: 'quote_sent',
      title: '📤 Quote Sent',
      message: `Quote ${quote.quote_number} sent to ${quote.customer_company_name}`,
      priority: 'low',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        amount: quote.total_amount,
        sent_at: quote.sent_at,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.success(`Quote sent successfully`, {
      description: `${quote.quote_number} • ${quote.customer_company_name}`,
    });
  }

  /**
   * Notify when quote is accepted
   */
  notifyQuoteAccepted(quote: Quote) {
    const notification: Notification = {
      id: `accepted-${Date.now()}`,
      type: 'quote_accepted',
      title: '✅ Quote Accepted!',
      message: `Great news! ${quote.customer_company_name} accepted quote ${quote.quote_number} for $${quote.total_amount?.toLocaleString()}`,
      priority: 'high',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        amount: quote.total_amount,
        accepted_at: quote.accepted_at,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.success(`Quote Accepted! 🎉`, {
      description: `${quote.customer_company_name} • $${quote.total_amount?.toLocaleString()}`,
      action: {
        label: "View Quote",
        onClick: () => window.location.href = `/quotes/${quote.id}`,
      },
      duration: 8000,
    });
  }

  /**
   * Notify when quote is rejected
   */
  notifyQuoteRejected(quote: Quote) {
    const notification: Notification = {
      id: `rejected-${Date.now()}`,
      type: 'quote_rejected',
      title: '❌ Quote Rejected',
      message: `${quote.customer_company_name} rejected quote ${quote.quote_number}${quote.rejection_reason ? `. Reason: ${quote.rejection_reason}` : ''}`,
      priority: 'medium',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        rejection_reason: quote.rejection_reason,
        rejected_at: quote.rejected_at,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.error(`Quote Rejected`, {
      description: `${quote.customer_company_name} • ${quote.rejection_reason || 'No reason provided'}`,
    });
  }

  /**
   * Notify when quote is about to expire
   */
  notifyQuoteExpiring(quote: Quote, daysLeft: number) {
    const notification: Notification = {
      id: `expiring-${Date.now()}`,
      type: 'quote_expiring',
      title: '⏰ Quote Expiring Soon',
      message: `Quote ${quote.quote_number} for ${quote.customer_company_name} expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
      priority: daysLeft <= 2 ? 'high' : 'medium',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        days_left: daysLeft,
        valid_until: quote.valid_until,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    if (daysLeft <= 3) {
      toast.warning(`Quote Expiring Soon`, {
        description: `${quote.quote_number} expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
        action: {
          label: "View Quote",
          onClick: () => window.location.href = `/quotes/${quote.id}`,
        },
      });
    }
  }

  /**
   * Notify high-value quote
   */
  private notifyHighValueQuote(quote: Quote) {
    const notification: Notification = {
      id: `high-value-${Date.now()}`,
      type: 'high_value_quote',
      title: '💰 High Value Quote',
      message: `High value quote ${quote.quote_number} created: $${quote.total_amount?.toLocaleString()}. May require additional approval.`,
      priority: 'high',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        amount: quote.total_amount,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.info(`High Value Quote Created`, {
      description: `$${quote.total_amount?.toLocaleString()} • May require approval`,
    });
  }

  /**
   * Notify export-controlled item
   */
  private notifyExportControlled(quote: Quote) {
    const notification: Notification = {
      id: `export-${Date.now()}`,
      type: 'export_controlled',
      title: '🚨 Export Controlled',
      message: `Quote ${quote.quote_number} contains export-controlled items. Compliance review required.`,
      priority: 'urgent',
      quote_id: quote.id,
      metadata: {
        quote_number: quote.quote_number,
        customer: quote.customer_company_name,
        export_license_type: quote.export_license_type,
      },
      created_at: new Date().toISOString(),
      read: false,
    };

    this.addNotification(notification);

    toast.error(`Export Controlled Items`, {
      description: `${quote.quote_number} • Compliance review required`,
      duration: 10000,
    });
  }

  /**
   * Check for expiring quotes and send notifications
   */
  checkExpiringQuotes(quotes: Quote[]) {
    const now = new Date();

    quotes.forEach(quote => {
      if (!quote.valid_until || quote.status !== 'sent') return;

      const expiryDate = new Date(quote.valid_until);
      const daysLeft = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysLeft > 0 && daysLeft <= 7) {
        // Check if we already notified about this
        const alreadyNotified = this.notifications.some(
          n => n.type === 'quote_expiring' &&
               n.quote_id === quote.id &&
               Math.abs(new Date(n.created_at).getTime() - now.getTime()) < 24 * 60 * 60 * 1000
        );

        if (!alreadyNotified) {
          this.notifyQuoteExpiring(quote, daysLeft);
        }
      }
    });
  }

  /**
   * Add notification and trigger listeners
   */
  private addNotification(notification: Notification) {
    this.notifications.unshift(notification); // Add to beginning

    // Keep only last 100 notifications
    if (this.notifications.length > 100) {
      this.notifications = this.notifications.slice(0, 100);
    }

    // Trigger listeners
    this.triggerListeners();
  }

  /**
   * Get all notifications
   */
  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  /**
   * Get unread count
   */
  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.triggerListeners();
    }
  }

  /**
   * Mark all as read
   */
  markAllAsRead() {
    this.notifications.forEach(n => n.read = true);
    this.triggerListeners();
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    this.notifications = [];
    this.triggerListeners();
  }

  /**
   * Subscribe to notification updates
   */
  subscribe(listener: (notifications: Notification[]) => void) {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Trigger all listeners
   */
  private triggerListeners() {
    this.listeners.forEach(listener => listener(this.getNotifications()));
  }
}

// Export singleton
export const quoteNotificationService = new QuoteNotificationService();
